import { publicAnonKey as ANON_KEY } from './supabase/info';
import {
  API_BASE,
  isEdgeFunctionUrl,
  recoverAuthTokenAfterUnauthorized,
} from './supabase/client';
import {
  logEdgeTransportIssueOnce,
  markEdgeBackendAvailable,
  markEdgeBackendUnavailable,
} from './edgeNetworkState';
import { APP_BUILD_INFO } from './appBuildInfo';

/** Server stamps every API response with X-Min-Client-Built-At when a
 *  minimum-supported-client floor is set. If the local bundle was built
 *  before that timestamp, the user's session is broken — the backend
 *  may be sending shapes the old code can't parse. We dispatch a window
 *  event the UpdateNotificationBanner picks up to switch into the
 *  forced-upgrade modal. Single-shot per page life so we don't loop.
 *
 *  No-op when the header is absent (the default code-only-push case)
 *  or when the local build is at or after the floor. */
let forcedUpgradeDispatched = false;
function checkMinClientVersion(res: Response): void {
  if (forcedUpgradeDispatched) return;
  const minBuiltAt = res.headers.get('X-Min-Client-Built-At');
  if (!minBuiltAt) return;
  try {
    const minMs = Date.parse(minBuiltAt);
    const localMs = Date.parse(APP_BUILD_INFO.builtAt);
    if (!isFinite(minMs) || !isFinite(localMs)) return;
    if (localMs >= minMs) return;
    forcedUpgradeDispatched = true;
    const reason = res.headers.get('X-Min-Client-Reason') || undefined;
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('quantis:client-out-of-date', {
        detail: reason ? { reason } : {},
      }));
    }
  } catch { /* malformed header — ignore */ }
}

/**
 * Patches window.fetch to handle Supabase Edge Function cold starts.
 *
 * Problem: the function takes ~1.5s to boot from cold. During that window
 * Supabase's gateway returns 503 for ALL requests, including OPTIONS preflights,
 * causing the browser to block every cross-origin fetch with "Failed to fetch".
 *
 * Naïve per-request retries make this worse — 10+ concurrent requests each
 * spawning 4 retries creates a thundering herd that overwhelms the booting
 * instance and can prevent it from ever becoming ready.
 *
 * Solution — shared warm-up probe:
 *   1. First request to fail triggers ONE probe: a simple GET /ping with no
 *      Authorization header. No custom headers → no OPTIONS preflight needed.
 *      The browser sends it directly without a preflight.
 *   2. All other failing requests share the same probe promise instead of
 *      independently hammering the server.
 *   3. Once /ping responds with a non-5xx status the function is warm.
 *   4. Every waiting request retries once. Their OPTIONS preflights now succeed.
 *
 * AbortErrors are never retried — they mean the caller cancelled on purpose.
 *
 * Call installEdgeFunctionRetry() once before the app mounts (main.tsx).
 */

const EDGE_FN_BASE = API_BASE;
const PING_URL = `${EDGE_FN_BASE}/ping`;

const RETRY_STATUSES = new Set([502, 503, 504]);
const PROBE_BASE_DELAY_MS = 1500;
const MAX_PROBE_ATTEMPTS = 6; // ~1.5 + 3 + 4.5 + 6 + 7.5 + 9 = ~31s total
const KEEPALIVE_INTERVAL_MS = 4 * 60 * 1000; // 4 minutes
const KEEPALIVE_INITIAL_DELAY_MS = 10_000; // 10s after startup
const KEEPALIVE_CHECK_MS = 60_000;
const ACTIVITY_WRITE_GAP_MS = 30_000;
const EDGE_ACTIVITY_KEY = 'quantis:edge-last-activity';

/**
 * The keepalive exists to leave a live worker for the next click. Any edge
 * request does that, in any tab, so a ping is due only when the app is on
 * screen and nothing has reached the function for a whole interval. Each open
 * tab used to ping every 4 minutes whether or not anyone was looking.
 */
export function keepalivePingDue(now: number, lastActivityAt: number, hidden: boolean): boolean {
  return !hidden && now - lastActivityAt >= KEEPALIVE_INTERVAL_MS;
}

let lastActivityWrite = 0;
/** Shared across tabs through localStorage, written at most every ACTIVITY_WRITE_GAP_MS per tab. */
export function noteEdgeActivity(now = Date.now()): void {
  if (now - lastActivityWrite < ACTIVITY_WRITE_GAP_MS) return;
  lastActivityWrite = now;
  try { localStorage.setItem(EDGE_ACTIVITY_KEY, String(now)); } catch { /* the per-tab stamp still counts */ }
}

export function lastEdgeActivity(): number {
  try {
    const stored = Number(localStorage.getItem(EDGE_ACTIVITY_KEY));
    return Number.isFinite(stored) ? Math.max(stored, lastActivityWrite) : lastActivityWrite;
  } catch {
    return lastActivityWrite;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

function getUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return (input as Request).url;
}

/** Reports filed from an error screen, which must never end the session. */
function isSupportTicketUrl(url: string): boolean {
  const path = url.split('?')[0];
  return path.endsWith('/support-tickets') || path.endsWith('/access-requests');
}

function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError') ||
    (err as any)?.name === 'AbortError'
  );
}

function isRetryableError(err: unknown): boolean {
  if (isAbortError(err)) return false;
  return true; // TypeError: Failed to fetch, network errors, etc.
}

function getMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method.toUpperCase();
  if (typeof Request !== 'undefined' && input instanceof Request) {
    return input.method.toUpperCase();
  }
  return 'GET';
}

function canRetryTransportFailure(input: RequestInfo | URL, init?: RequestInit): boolean {
  const method = getMethod(input, init);
  return method === 'GET' || method === 'HEAD' || method === 'OPTIONS';
}

export function installEdgeFunctionRetry(): void {
  if (typeof window === 'undefined') return;

  const nativeFetch = window.fetch.bind(window);

  // ─── Shared warm-up probe ────────────────────────────────────────────────
  // At most ONE probe runs at a time. All requests that hit a cold-start
  // error queue up here and share the result.
  let warmUpPromise: Promise<void> | null = null;

  function getWarmUpPromise(): Promise<void> {
    if (warmUpPromise) return warmUpPromise;

    warmUpPromise = (async () => {
      for (let attempt = 0; attempt < MAX_PROBE_ATTEMPTS; attempt++) {
        if (attempt > 0) {
          await sleep(PROBE_BASE_DELAY_MS * attempt);
        }
        try {
          // No Authorization header → qualifies as a "simple request" →
          // browser sends it directly with NO OPTIONS preflight.
          const res = await nativeFetch(PING_URL);
          // Drain body so connection is released
          await res.text().catch(() => {});
          if (!RETRY_STATUSES.has(res.status)) {
            return; // ✅ function is warm
          }
        } catch {
          // network error — keep probing
        }
      }
      // Exhausted probes — give up (callers will propagate their errors)
    })().finally(() => {
      warmUpPromise = null;
    });

    return warmUpPromise;
  }

  // ─── Patched window.fetch ────────────────────────────────────────────────
  window.fetch = async function patchedFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    const url = getUrl(input);

    // Pass through anything that isn't our edge function
    if (!isEdgeFunctionUrl(url)) {
      return nativeFetch(input, init);
    }
    noteEdgeActivity();

    // Keep an untouched request template so both auth recovery and cold-start
    // recovery can replay POST/PUT bodies safely. Each send consumes a clone,
    // never the template itself.
    const requestTemplate = new Request(input, init);
    let replacementToken: string | null = null;
    let authRecoveryAttempted = false;

    const bearerToken = (): string | null => {
      const value = requestTemplate.headers.get('Authorization');
      const match = value?.match(/^Bearer\s+(.+)$/i);
      const token = match?.[1]?.trim() || null;
      if (!token || token === ANON_KEY) return null;
      return token;
    };

    const requestForSend = (): Request => {
      const request = requestTemplate.clone();
      if (!replacementToken) return request;
      const headers = new Headers(request.headers);
      headers.set('Authorization', `Bearer ${replacementToken}`);
      return new Request(request, { headers });
    };

    const sendWithAuthRecovery = async (): Promise<Response> => {
      let response = await nativeFetch(requestForSend());
      markEdgeBackendAvailable();

      // A crash report filed with a dead session must not trigger token
      // recovery, which refreshes or signs the user out. It still gets the
      // cold-start warm-up and replay like every other edge request.
      if (response.status !== 401 || authRecoveryAttempted || isSupportTicketUrl(url)) return response;
      const rejectedToken = replacementToken ?? bearerToken();
      if (!rejectedToken) return response;

      authRecoveryAttempted = true;
      if (requestTemplate.signal.aborted) {
        await response.body?.cancel().catch(() => {});
        throw new DOMException('The operation was aborted.', 'AbortError');
      }
      const recovery = recoverAuthTokenAfterUnauthorized(rejectedToken);
      let onAbort: (() => void) | undefined;
      const aborted = new Promise<never>((_, reject) => {
        onAbort = () => reject(new DOMException('The operation was aborted.', 'AbortError'));
        if (requestTemplate.signal.aborted) {
          onAbort();
          return;
        }
        requestTemplate.signal.addEventListener('abort', onAbort, { once: true });
      });
      let recoveredToken: string | null;
      try {
        recoveredToken = await Promise.race([recovery, aborted]);
      } catch (error) {
        if (isAbortError(error)) {
          await response.body?.cancel().catch(() => {});
        }
        throw error;
      } finally {
        if (onAbort) requestTemplate.signal.removeEventListener('abort', onAbort);
      }
      if (!recoveredToken || recoveredToken === rejectedToken) return response;

      replacementToken = recoveredToken;
      await response.body?.cancel().catch(() => {});
      response = await nativeFetch(requestForSend());
      markEdgeBackendAvailable();
      return response;
    };

    // Helper: abort-aware check before attempting anything
    const checkAborted = () => {
      if (requestTemplate.signal.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError');
      }
    };

    // ── First attempt ────────────────────────────────────────────────────
    checkAborted();
    let firstError: unknown = null;
    let firstResponse: Response | undefined;

    try {
      const res = await sendWithAuthRecovery();
      // Inference may already have incurred cost or saved actions. Its caller
      // owns bounded retries; the global cold-start wrapper must not replay it.
      if (!RETRY_STATUSES.has(res.status) || /\/takeoff-ai\/computer\/turn(?:\?|$)/.test(url)) {
        checkMinClientVersion(res);
        return res;
      }
      const retryDetail = await res.clone().json().catch(() => null);
      if (retryDetail?.retryable === false || retryDetail?.billableResponse === true) {
        checkMinClientVersion(res);
        return res;
      }
      // A running application can return 503 because a required query failed.
      // Replaying that full snapshot after /ping only multiplies the same load.
      // Inspect a clone so the caller retains the structured error body.
      if (res.status === 503) {
        const detail = await res.clone().json().catch(() => null);
        if (detail?.code === 'DATABASE_REFRESH_UNAVAILABLE') {
          checkMinClientVersion(res);
          return res;
        }
      }
      // Gateway 503/502/504 — drain and fall through to warm-up
      await res.text().catch(() => {});
      firstResponse = res;
    } catch (err) {
      if (isAbortError(err)) throw err;
      if (/\/takeoff-ai\/computer\/turn(?:\?|$)/.test(url)) throw err;
      firstError = err;
      markEdgeBackendUnavailable(err);
      logEdgeTransportIssueOnce('edgeFunctionRetry', `${getMethod(input, init)} ${url}`);
    }

    // ── Wait for the shared warm-up probe ────────────────────────────────
    checkAborted();

    // Race the warm-up against the caller's AbortSignal so we don't block
    // longer than the caller wants to wait.
    const abortRace = new Promise<never>((_, reject) => {
      if (!requestTemplate.signal) return;
      if (requestTemplate.signal.aborted) {
        reject(new DOMException('The operation was aborted.', 'AbortError'));
        return;
      }
      requestTemplate.signal.addEventListener(
        'abort',
        () => reject(new DOMException('The operation was aborted.', 'AbortError')),
        { once: true },
      );
    });

    try {
      await Promise.race([getWarmUpPromise(), abortRace]);
    } catch (err) {
      // If the abort signal fired during warm-up, propagate AbortError
      if (isAbortError(err)) throw err;
      // Warm-up probe failed — propagate original error
      if (firstError) throw firstError;
      return firstResponse!;
    }

    // ── Retry the original request now that the function is warm ─────────
    checkAborted();
    try {
      const res = await sendWithAuthRecovery();
      checkMinClientVersion(res);
      return res;
    } catch (err) {
      if (isAbortError(err)) throw err;
      markEdgeBackendUnavailable(err);
      if (!canRetryTransportFailure(input, init) && firstError) {
        throw firstError;
      }
      // If this also fails, throw the original error for a cleaner message
      throw firstError ?? err;
    }
  } as typeof window.fetch;

  // ─── Keepalive ───────────────────────────────────────────────────────────
  // An idle but visible session pings so the next click finds a live worker.
  // Uses the patched fetch so it goes through the warm-up probe too if needed,
  // and so the ping itself counts as activity for every other tab.
  function keepaliveTick() {
    if (!keepalivePingDue(Date.now(), lastEdgeActivity(), document.hidden)) return;
    window
      .fetch(`${EDGE_FN_BASE}/ping`, {
        headers: { Authorization: `Bearer ${ANON_KEY}` },
      })
      .catch(() => {});
  }

  setTimeout(() => {
    keepaliveTick();
    setInterval(keepaliveTick, KEEPALIVE_CHECK_MS);
  }, KEEPALIVE_INITIAL_DELAY_MS);
}
